import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import FilterBar from '../../components/filters/FilterBar';
import DataTable from '../../components/ui/DataTable';
import AreaChart from '../../components/ui/AreaChart';
import BarChart from '../../components/ui/BarChart';
import MetricCard from '../../components/dashboard/core/MetricCard';
import { useFilterPersistence } from '../../hooks/useFilterPersistence';
import { useUserAnalytics, useSplitAnalytics, useTransactionAnalytics, useSharedWalletAnalytics, useBadgeAnalytics, usePhoneAuthAnalytics } from '../../hooks/useAnalytics';
import { transformToChartDataAsync } from '../../services/chartService';
import { exportToCsv, exportToJson } from '../../utils/exportService';
import { FILTER_PERIODS } from '../../constants';
import { useFormatting } from '../../hooks/useFormatting';
import './MetricDetails.css';

/**
 * Metric Details Page
 * Comprehensive detail page for any metric clicked from dashboard
 */
const MetricDetails = () => {
  const { metricId } = useParams();
  const navigate = useNavigate();
  const { period, setPeriod, platform, setPlatform } = useFilterPersistence({
    pageKey: `details-${metricId}`,
    defaultPeriod: FILTER_PERIODS.ALL,
    defaultPlatform: null
  });

  // Fetch all analytics data
  const { data: userData, loading: userLoading } = useUserAnalytics(period, platform);
  const { data: splitData, loading: splitLoading } = useSplitAnalytics(period);
  const { data: txData, loading: txLoading } = useTransactionAnalytics(period);
  const { data: walletData, loading: walletLoading } = useSharedWalletAnalytics(period);
  const { data: badgeData, loading: badgeLoading } = useBadgeAnalytics(period);
  const { data: phoneAuthData, loading: phoneAuthLoading } = usePhoneAuthAnalytics(period);

  const { formatCurrency, formatNumber } = useFormatting();
  const isLoading = userLoading || splitLoading || txLoading || walletLoading || badgeLoading || phoneAuthLoading;

  // Metric configuration mapping
  const metricConfig = useMemo(() => {
    const configs = {
      'total-users': {
        title: 'Total Users',
        description: 'Complete user analytics and data',
        dataSource: 'users',
        getValue: (data) => data?.totalUserCount || 0,
        getTableData: async (data) => {
          // Fetch raw user data from Firestore
          const { collection, getDocs } = await import('firebase/firestore');
          const { db } = await import('../../../firebase/config');
          const usersRef = collection(db, 'users');
          const snapshot = await getDocs(usersRef);
          return snapshot.docs.map(doc => {
            const docData = doc.data();
            // Check snake_case (primary) first, then camelCase (fallback)
            const createdAt = docData.created_at?.toDate?.() || docData.created_at || 
                             docData.createdAt?.toDate?.() || docData.createdAt || null;
            return {
              id: doc.id,
              ...docData,
              createdAt,
              // Normalize field names for display (check both variations)
              emailVerified: docData.email_verified ?? docData.emailVerified ?? false,
              lastLoginAt: docData.last_login_at || docData.lastLoginAt || null,
              walletAddress: docData.wallet_address || docData.walletAddress || null
            };
          });
        },
        getChartData: async (data) => await transformToChartDataAsync('users', 'totalUserCount', period),
        columns: [
          { key: 'id', label: 'User ID' },
          { key: 'email', label: 'Email' },
          { key: 'name', label: 'Name' },
          { key: 'createdAt', label: 'Signup Date', type: 'date' },
          { key: 'emailVerified', label: 'Verified', type: 'boolean' },
          { key: 'hasCompletedOnboarding', label: 'Onboarded', type: 'boolean' }
        ]
      },
      'transaction-volume': {
        title: 'Transaction Volume',
        description: 'All transaction data and analytics',
        dataSource: 'transactions',
        getValue: (data) => data?.totalTransactionValue || 0,
        getTableData: (data) => data?.transactions || [],
        getChartData: async (data) => await transformToChartDataAsync('transactions', 'totalTransactionValue', period),
        columns: [
          { key: 'id', label: 'Transaction ID' },
          { key: 'userId', label: 'User ID' },
          { key: 'amount', label: 'Amount', type: 'currency' },
          { key: 'type', label: 'Type' },
          { key: 'status', label: 'Status' },
          { key: 'createdAt', label: 'Date', type: 'date' }
        ]
      },
      'total-splits': {
        title: 'Total Splits',
        description: 'Complete split analytics and data',
        dataSource: 'splits',
        getValue: (data) => data?.totalSplits || 0,
        getTableData: (data) => data?.splits || [],
        getChartData: async (data) => await transformToChartDataAsync('splits', 'totalSplits', period),
        columns: [
          { key: 'id', label: 'Split ID' },
          { key: 'creatorId', label: 'Creator' },
          { key: 'amount', label: 'Amount', type: 'currency' },
          { key: 'status', label: 'Status' },
          { key: 'participants', label: 'Participants', type: 'number' },
          { key: 'createdAt', label: 'Created', type: 'date' }
        ]
      },
      'active-users': {
        title: 'Active Users',
        description: 'Active user analytics and engagement data',
        dataSource: 'users',
        getValue: (data) => data?.activeUsers || 0,
        getTableData: (data) => (data?.users || []).filter(u => {
          // Check snake_case (primary) first, then camelCase (fallback)
          const lastLoginValue = u.last_login_at || u.lastLoginAt;
          const lastLogin = lastLoginValue ? new Date(lastLoginValue) : null;
          if (!lastLogin) return false;
          const daysSince = (Date.now() - lastLogin.getTime()) / (1000 * 60 * 60 * 24);
          return daysSince <= 7;
        }).map(u => ({
          ...u,
          // Normalize field names for display
          lastLoginAt: u.last_login_at || u.lastLoginAt || null,
          emailVerified: u.email_verified ?? u.emailVerified ?? false
        })),
        getChartData: async (data) => await transformToChartDataAsync('users', 'activeUsers', period),
        columns: [
          { key: 'id', label: 'User ID' },
          { key: 'email', label: 'Email' },
          { key: 'name', label: 'Name' },
          { key: 'lastLoginAt', label: 'Last Login', type: 'date' },
          { key: 'emailVerified', label: 'Verified', type: 'boolean' }
        ]
      }
    };

    return configs[metricId] || {
      title: 'Metric Details',
      description: 'Detailed analytics for this metric',
      dataSource: null,
      getValue: () => 0,
      getTableData: () => [],
      getChartData: async () => null,
      columns: []
    };
  }, [metricId, period]);

  // Get current data based on metric
  const currentData = useMemo(() => {
    switch (metricConfig.dataSource) {
      case 'users': return userData;
      case 'splits': return splitData;
      case 'transactions': return txData;
      case 'wallets': return walletData;
      case 'badges': return badgeData;
      case 'phoneAuth': return phoneAuthData;
      default: return null;
    }
  }, [metricConfig.dataSource, userData, splitData, txData, walletData, badgeData, phoneAuthData]);

  // Get table data
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);

  useEffect(() => {
    if (!currentData || !metricConfig.getTableData) {
      setTableData([]);
      setTableLoading(false);
      return;
    }

    const fetchTableData = async () => {
      setTableLoading(true);
      try {
        const data = await metricConfig.getTableData(currentData);
        setTableData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching table data:', error);
        setTableData([]);
      } finally {
        setTableLoading(false);
      }
    };

    fetchTableData();
  }, [currentData, metricConfig]);

  // Chart data state
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (!currentData) return;
    metricConfig.getChartData(currentData).then(setChartData).catch(console.error);
  }, [currentData, metricConfig]);

  // Handle export
  const handleExport = (format) => {
    if (!tableData || tableData.length === 0) {
      alert('No data available to export');
      return;
    }

    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${metricId}-${period}-${timestamp}.${format}`;

    if (format === 'csv') {
      exportToCsv(tableData, filename, metricConfig.columns);
    } else {
      exportToJson(tableData, filename);
    }
  };

  const filters = (
    <FilterBar
      period={period}
      onPeriodChange={setPeriod}
      platform={platform}
      onPlatformChange={setPlatform}
      rightActions={
        <div className="filter-bar-right">
          <button className="admin-btn-secondary" onClick={() => handleExport('csv')}>
            <i className="bi bi-download"></i> Export CSV
          </button>
          <button className="admin-btn-secondary" onClick={() => handleExport('json')}>
            <i className="bi bi-download"></i> Export JSON
          </button>
          <button className="admin-btn-secondary" onClick={() => navigate('/dashboard')}>
            <i className="bi bi-arrow-left"></i> Back to Dashboard
          </button>
        </div>
      }
    />
  );

  const currentValue = metricConfig.getValue(currentData);

  return (
    <PageLayout
      title={metricConfig.title}
      description={metricConfig.description}
      filters={filters}
    >
      <div className="metric-details-content">
        {/* Summary Cards */}
        <div className="metric-details-summary">
          <MetricCard
            title="Current Value"
            value={currentValue}
            formatType={typeof currentValue === 'number' && currentValue > 1000 ? 'number' : null}
            loading={isLoading}
            size="large"
          />
          <MetricCard
            title="Total Records"
            value={tableData.length}
            formatType="number"
            loading={isLoading}
            size="large"
          />
        </div>

        {/* Chart Section */}
        {chartData && (
          <div className="metric-details-chart">
            <h3>Trend Over Time</h3>
            <AreaChart
              data={chartData}
              height={300}
              showLegend={true}
            />
          </div>
        )}

        {/* Data Table */}
        <div className="metric-details-table">
          <h3>All Data</h3>
          <DataTable
            data={tableData}
            columns={metricConfig.columns}
            loading={isLoading || tableLoading}
            pagination={true}
            searchable={true}
            sortable={true}
          />
        </div>
      </div>
    </PageLayout>
  );
};

export default MetricDetails;

